import { useEffect, useState, useCallback } from 'react'

/**
 * Custom hook for managing highlight navigation
 * 
 * @param {Object} scrollableRef - Ref to the scrollable container
 * @param {Array} highlightRanges - Array of highlight range objects
 * @param {Object} highlightAnchorsRef - Ref containing array of anchor refs for each highlight
 * @returns {Object} Navigation state and controls
 */
const useHighlightNavigation = (scrollableRef, highlightRanges, highlightAnchorsRef) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isNavigationNeeded, setIsNavigationNeeded] = useState(false)
    const [hasUserNavigated, setHasUserNavigated] = useState(false)

    const totalHighlights = Array.isArray(highlightRanges) ? highlightRanges.length : 0

    // Check if navigation is needed (highlights extend beyond viewport)
    useEffect(() => {
        const scrollableEl = scrollableRef.current
        if (!scrollableEl || totalHighlights === 0) {
            setIsNavigationNeeded(false)
            return
        }

        const checkNavigationNeeded = () => {
            const containerHeight = scrollableEl.clientHeight
            const scrollHeight = scrollableEl.scrollHeight
            const isScrollable = scrollHeight > containerHeight

            // Show navigation when content is scrollable and there are highlights
            // Navigation is useful whenever there's scroll, as highlights might not all be visible
            setIsNavigationNeeded(isScrollable && totalHighlights > 0)
        }

        // Small delay to ensure anchors are mounted
        const timer = setTimeout(checkNavigationNeeded, 100)
        
        const ro = new ResizeObserver(checkNavigationNeeded)
        ro.observe(scrollableEl)
        
        return () => {
            clearTimeout(timer)
            ro.disconnect()
        }
    }, [scrollableRef, totalHighlights, highlightAnchorsRef])

    // Reset current index and navigation state when highlights change
    useEffect(() => {
        setCurrentIndex(totalHighlights > 0 ? 0 : -1)
        setHasUserNavigated(false)
    }, [highlightRanges, totalHighlights])

    /**
     * Scroll to a specific highlight, making it the first visible line
     */
    const scrollToHighlight = useCallback(
        index => {
            const scrollableEl = scrollableRef.current
            const anchorRef = highlightAnchorsRef.current[index]

            if (!scrollableEl || !anchorRef || !anchorRef.current) return

            const anchor = anchorRef.current
            const anchorTop = anchor.offsetTop

            // Scroll so the highlight appears near the top with a small offset
            const offset = 20 // pixels from top
            scrollableEl.scrollTo({
                top: Math.max(0, anchorTop - offset),
                behavior: 'smooth',
            })

            setHasUserNavigated(true)
            setCurrentIndex(index)
        },
        [scrollableRef, highlightAnchorsRef],
    )

    /**
     * Navigate to the previous highlight
     */
    const goToPrevious = useCallback(() => {
        if (currentIndex > 0) {
            scrollToHighlight(currentIndex - 1)
        }
    }, [currentIndex, scrollToHighlight])

    /**
     * Navigate to the next highlight
     */
    const goToNext = useCallback(() => {
        if (currentIndex < totalHighlights - 1) {
            scrollToHighlight(currentIndex + 1)
        }
    }, [currentIndex, totalHighlights, scrollToHighlight])

    const canGoPrevious = currentIndex > 0
    const canGoNext = currentIndex < totalHighlights - 1
    const showNavigation = isNavigationNeeded && totalHighlights > 0

    return {
        currentIndex,
        totalHighlights,
        canGoPrevious,
        canGoNext,
        showNavigation,
        goToPrevious,
        goToNext,
        scrollToHighlight,
        hasUserNavigated,
    }
}

export default useHighlightNavigation

